import React, { useRef } from 'react';
import { Card, Spin } from 'antd';
import { colors, formatNum, getPieChartOption } from '../utils/helper';
import { PolicySummary } from '../utils/types';
import PopOverMenu from '../components/popover';
import { SUMMARY_TITLE, SUMMARY_CONTENT } from '../utils/const';
import ReactECharts from 'echarts-for-react';
import {
  SummaryTableHeader,
  SummaryTable,
  PieChartDiv,
  CDNInfoDiv,
  CNDNameDiv,
  SummarySum,
  SummaryTitle,
  SummarySpan,
  SummaryDigit,
  CDNText,
} from '../utils/styles';

interface SummaryCardProps {
  summaryData?: PolicySummary; // Adjust type as needed
}

export const SummaryCard = (prop: SummaryCardProps) => {
  const { summaryData } = prop;
  const chartRef = useRef<any>(null);
  const cdnRef = useRef<Record<string, HTMLSpanElement | null>>({});

  const hoveredCDNRef = useRef<string | null>(null);

  const syncHighlight = (cdnName: string | null) => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart || cdnName === hoveredCDNRef.current) return;

    if (hoveredCDNRef.current) {
      const prevIndex = summaryData?.total_resources.findIndex(
        (c) => c.cdn_name === hoveredCDNRef.current,
      );
      if (prevIndex !== -1) {
        chart.dispatchAction({
          type: 'downplay',
          seriesIndex: 0,
          dataIndex: prevIndex,
        });
      }
      Object.values(cdnRef.current).forEach((el) => {
        if (el) el.style.backgroundColor = 'transparent';
      });
    }

    if (cdnName) {
      const newIndex = summaryData?.total_resources.findIndex((c) => c.cdn_name === cdnName);
      if (newIndex !== -1) {
        chart.dispatchAction({
          type: 'highlight',
          seriesIndex: 0,
          dataIndex: newIndex,
        });
        const el = cdnRef.current[cdnName];
        if (el) el.style.backgroundColor = '#938c8c';
      }
    }

    hoveredCDNRef.current = cdnName;
  };

  if (!summaryData) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Loading summary data..." />
        </div>
      </Card>
    );
  }

  return (
    <div>
      <SummaryTitle>Summary</SummaryTitle>
      <PopOverMenu summary={SUMMARY_TITLE} content={SUMMARY_CONTENT} />

      <SummaryTable>
        <SummaryTableHeader style={{ borderRight: '0.5px solid #bbbbbb' }}>
          Total
        </SummaryTableHeader>
        <SummaryTableHeader />
        <SummaryTableHeader>CDN</SummaryTableHeader>
        <SummaryTableHeader style={{ textAlign: 'right' }}>Requests per CDN</SummaryTableHeader>
        <SummaryTableHeader />
        <SummarySum>
          <SummaryDigit>{summaryData?.total_policies}</SummaryDigit>
          policies
          <div style={{ marginBottom: '30px' }}></div>
          <SummaryDigit>{formatNum(summaryData?.total_requests || 0)}</SummaryDigit>
          requests
        </SummarySum>

        <PieChartDiv>
          <ReactECharts
            ref={chartRef}
            option={getPieChartOption(summaryData?.total_resources || [])}
            style={{ width: '250px', height: '250px', marginLeft: '20%', marginTop: '10%' }}
            onEvents={{
              mouseover: (params) => params?.name && syncHighlight(params.name),
              mouseout: () => syncHighlight(null),
            }}
          />
        </PieChartDiv>

        <div style={{ gridColumn: 'span 2' }}>
          {summaryData?.total_resources.map((cdn, index) => (
            <CDNInfoDiv
              key={cdn.cdn_name}
              ref={(el) => {
                cdnRef.current[cdn.cdn_name] = el;
              }}
              onMouseEnter={() => syncHighlight(cdn.cdn_name)}
              onMouseLeave={() => syncHighlight(null)}
            >
              {/* CDN */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <CNDNameDiv style={{ backgroundColor: colors[index % colors.length] }} />
                <CDNText>{cdn.cdn_name}</CDNText>
              </div>

              {/* Requests */}
              <div style={{ display: 'flex', gap: '5px' }}>
                <SummarySpan>{formatNum(cdn.cdn_requests || 0)}</SummarySpan>
                <SummarySpan>
                  ({' '}
                  {summaryData?.total_requests > 0
                    ? (((cdn.cdn_requests || 0) / summaryData?.total_requests) * 100).toFixed(1)
                    : 0}
                  % )
                </SummarySpan>
              </div>
            </CDNInfoDiv>
          ))}
        </div>
      </SummaryTable>
    </div>
  );
};
